import React, { useEffect, useState } from 'react';
import api from '../services/api';
import PageHero from '../components/layout/PageHero';
import {
  CpuChipIcon,
  CodeBracketIcon,
  ShieldCheckIcon,
  CircleStackIcon,
  ArrowRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

type ResearchDirection = {
  _id?: string;
  order: number;
  title: string;
  summary: string;
  description: string;
  bullets: string[];
};

type ResearchPageData = {
  intro: string;
  directions: ResearchDirection[];
};

const renderHtml = (html?: string) => ({ __html: html || '' });

const Research: React.FC = () => {
  const [data, setData] = useState<ResearchPageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get<ResearchPageData>('/research-page');
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="bg-gradient-to-b from-white via-white to-brand-redSoft/40 px-4 py-12">
        <p className="text-center text-sm text-neutral-600">Loading...</p>
      </div>
    );
  }

  const intro = data?.intro ?? '';
  const directions = data?.directions ?? [];
  const shownDirections = directions.slice(0, 4);

  const iconByIndex = [CpuChipIcon, CodeBracketIcon, ShieldCheckIcon, CircleStackIcon];
  const subtitleByIndex = [
    'Efficient Energy Conversion',
    'Multiscale Materials Design',
    'Electrocatalytic Mechanisms',
    'Interdisciplinary Integration',
  ];

  return (
    <div className="bg-surface min-h-full">
      <PageHero
        title="Research"
        intro={
          intro ||
          'The group explores frontier problems in energy and materials science, from fundamental mechanisms to practical engineering pathways.'
        }
        sectionLabel="Research Momentum"
      />

      <section className="w-full bg-surface px-8 pb-20 pt-12 relative">
        <div className="pointer-events-none absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-surface-container-low/50 to-transparent" />
        <div className="mx-auto max-w-screen-2xl relative z-10">
          {shownDirections.length === 0 ? (
            <div className="rounded-md bg-surface-container-low p-10 text-center text-on-surface-variant">
              No research content yet. Please add it in admin Research management.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              {shownDirections.map((d, index) => (
                <article
                  key={d._id ?? index}
                  className="um-card relative h-full overflow-hidden bg-surface-container-lowest p-8 transition-colors duration-500 hover:bg-surface-container lg:col-span-4"
                >
                  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-surface-container text-primary transition-colors duration-500">
                    {React.createElement(iconByIndex[index % iconByIndex.length], {
                      className: 'h-7 w-7',
                    })}
                  </div>
                  <h2
                    className="mb-3 text-3xl font-semibold leading-tight text-primary-container"
                    dangerouslySetInnerHTML={renderHtml(d.title || 'Untitled Direction')}
                  />
                  <h3 className="mb-4 text-sm font-bold uppercase tracking-widest text-on-surface-variant">
                    {subtitleByIndex[index % subtitleByIndex.length]}
                  </h3>
                  <p
                    className="mb-7 text-base leading-relaxed text-on-surface-variant"
                    dangerouslySetInnerHTML={renderHtml(d.description || d.summary || 'Description is being prepared.')}
                  />
                  <div className="mt-auto">
                    <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-primary">
                      Highlights
                    </h4>
                    <ul className="space-y-2 text-sm text-on-surface-variant">
                      {(d.bullets || []).slice(0, 3).map((b, i) => (
                        <li key={i} className="flex items-start">
                          <ArrowRightIcon className="mr-2 mt-0.5 h-4 w-4 text-primary" />
                          <span dangerouslySetInnerHTML={renderHtml(b)} />
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}

              <article className="um-card-soft flex h-full flex-col items-center justify-center border-dashed border-outline-variant/40 bg-surface-container-low/30 p-8 text-center lg:col-span-4">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface text-on-surface-variant/50">
                  <PlusIcon className="h-7 w-7" />
                </div>
                <h2 className="mb-2 text-3xl font-semibold leading-tight text-on-surface-variant/70">Emerging Direction</h2>
                <p className="max-w-[220px] text-sm text-on-surface-variant/60">
                  New research directions are being planned and will be updated continuously.
                </p>
              </article>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Research;
